import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Thermometer, AlertCircle, ChevronLeft, ChevronRight, Timer, Info, PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Recipe } from "@/types/recipe";
import { CcpStepFlag } from "@/components/recipes/ccp";
import { ccpStepNumber, isCcpStep } from "@/lib/recipes/ccp";
import {
  type RecipeStep,
  StepSidebarCard,
  MiniTimer,
  formatTimer,
  toVideoEmbedUrl,
  timerToSeconds,
} from "@/components/recipes/execution-steps";

interface ExecutionStepsTabProps {
  recipe: Recipe;
  onEdit?: () => void;
}

export function ExecutionStepsTab({ recipe, onEdit }: ExecutionStepsTabProps) {
  const { t } = useLingui();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const steps: RecipeStep[] = Array.isArray(recipe.instructions)
    ? (recipe.instructions as RecipeStep[])
    : [];

  if (steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-12">
        <p className="text-sm text-(--color-text-secondary)">{t`No execution steps defined yet.`}</p>
        {onEdit && (
          <Button variant="link" onClick={onEdit} className="h-auto p-0 text-sm text-(--color-text-primary)">
            {t`Add steps`}
          </Button>
        )}
      </div>
    );
  }

  const selected = (steps[selectedIndex] ?? steps[0])!;

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <div className="flex items-center justify-between pl-2">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-medium leading-none text-(--color-text-primary)">
            {t`Execution Steps`}
          </h2>
          <div className="flex w-10 items-center justify-center rounded-full bg-(--color-surface-muted) px-2 py-1">
            <span className="text-sm font-medium text-(--color-text-secondary)">{steps.length}</span>
          </div>
        </div>
        {onEdit && (
          <Button variant="brand-outline" onClick={onEdit}>
            <PencilLine className="size-5" />
            {t`Edit`}
          </Button>
        )}
      </div>

      {/* Two-panel container */}
      <div className="flex gap-3 rounded-[10px] border border-(--color-border) bg-(--color-surface-muted) p-3">
        {/* Left sidebar */}
        <div className="flex w-100 shrink-0 flex-col gap-2.5 self-start rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-3">
          <div className="px-3 py-3">
            <h3 className="text-xl font-medium text-(--color-text-primary)">{t`Steps`}</h3>
          </div>
          <div className="flex flex-col gap-2">
            {steps.map((step, i) => (
              <StepSidebarCard
                key={i}
                step={step}
                index={i}
                isSelected={i === selectedIndex}
                onClick={() => setSelectedIndex(i)}
                ccpFlag={
                  isCcpStep(step) ? (
                    <CcpStepFlag
                      recipeId={recipe.id}
                      number={ccpStepNumber(step, i)}
                      displayNumber={i + 1}
                      text={step.instruction ?? ""}
                    />
                  ) : undefined
                }
              />
            ))}
          </div>
        </div>

        {/* Right panel */}
        <StepDetailPanel
          key={selectedIndex}
          step={selected}
          stepIndex={selectedIndex}
          totalSteps={steps.length}
          onPrev={() => setSelectedIndex((i) => i - 1)}
          onNext={() => setSelectedIndex((i) => i + 1)}
        />
      </div>
    </div>
  );
}

// ── Step detail panel ─────────────────────────────────────────────────────────

function StepDetailPanel({
  step,
  stepIndex,
  totalSteps,
  onPrev,
  onNext,
}: {
  step: RecipeStep;
  stepIndex: number;
  totalSteps: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useLingui();
  const hasTimer = step.timerEnabled && (step.timer ?? 0) > 0;
  const timerSeconds = hasTimer
    ? timerToSeconds(step.timer!, step.timerUnit)
    : 0;
  const videoEmbedUrl = toVideoEmbedUrl(step.youtubeUrl);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-3">
      {/* Header */}
      <div className="flex items-start justify-between p-3">
        <div className="flex flex-col gap-2.5">
          <div className="flex w-fit items-center justify-center rounded-full bg-(--color-surface-muted) px-3 py-2">
            <span className="text-sm font-medium text-(--color-text-secondary)">
              {t`Step`} {stepIndex + 1}
            </span>
          </div>
          <h3 className="text-2xl font-medium leading-none text-(--color-text-primary)">
            {step.instruction || t`Untitled step`}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="brand-outline" onClick={onPrev} disabled={stepIndex === 0}>
            <ChevronLeft className="size-5" />
            {t`Prev`}
          </Button>
          <Button variant="brand-outline" onClick={onNext} disabled={stepIndex === totalSteps - 1}>
            {t`Next`}
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>

      {/* Info cards row */}
      <InfoCardsRow step={step} hasTimer={!!hasTimer} timerSeconds={timerSeconds} />

      {/* Text sections */}
      {step.techniqueNotes && (
        <TextDetail label={t`Technique Notes`} value={step.techniqueNotes} />
      )}
      {step.warningEnabled && step.warnings && (
        <TextDetail label={t`Warnings`} value={step.warnings} />
      )}

      {videoEmbedUrl && (
        <div className="flex flex-col gap-3 px-3 py-2">
          <p className="text-base font-medium text-(--color-text-primary)">{t`Video`}</p>
          <div className="relative aspect-video w-full overflow-hidden rounded-[12px] bg-(--color-surface-muted)">
            <iframe
              src={videoEmbedUrl}
              title={step.instruction || t`Execution step video`}
              className="absolute inset-0 h-full w-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Media */}
      {(step.mediaUrls?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-3 px-3 py-2">
          <div className="flex items-center gap-3">
            <p className="text-base font-medium text-(--color-text-primary)">{t`Media`}</p>
            <div className="flex w-10 items-center justify-center rounded-full bg-(--color-surface-muted) px-3 py-2">
              <span className="text-sm font-medium text-(--color-text-secondary)">
                {step.mediaUrls!.length}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {step.mediaUrls!.map((url, i) => (
              <div
                key={i}
                className="relative h-50 w-75 shrink-0 overflow-hidden rounded-[12px] bg-(--color-surface-muted)"
              >
                <img
                  src={url}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Info cards row ────────────────────────────────────────────────────────────

function InfoCardsRow({
  step,
  hasTimer,
  timerSeconds,
}: {
  step: RecipeStep;
  hasTimer: boolean;
  timerSeconds: number;
}) {
  const { t } = useLingui();

  return (
    <div className="flex gap-2 rounded-[10px] bg-(--color-surface-muted) p-2">
      {/* Timer card */}
      <InfoCard
        icon={<Timer className="size-5 text-(--color-text-secondary)" />}
        label={t`Timer`}
        value={
          hasTimer ? (
            <div className="flex w-fit items-center gap-1 rounded-full bg-(--brand-blue) px-2 py-1">
              <Timer className="size-4 text-white" />
              <span className="text-sm font-medium text-white">
                {formatTimer(step.timer!, step.timerUnit)}
              </span>
            </div>
          ) : (
            <span className="text-sm text-(--color-text-secondary)">—</span>
          )
        }
        footer={hasTimer ? <MiniTimer initialSeconds={timerSeconds} /> : undefined}
      />

      {/* Temperature card */}
      <InfoCard
        icon={<Thermometer className="size-5 text-(--color-text-secondary)" />}
        label={t`Temperature`}
        value={
          step.temperature ? (
            <p className="text-base font-medium text-(--color-text-primary)">
              {step.temperature}°{step.temperatureUnit ?? "C"} holding
            </p>
          ) : (
            <span className="text-sm text-(--color-text-secondary)">—</span>
          )
        }
        footer={<InfoNote>{t`Optional; shown in Cook Mode.`}</InfoNote>}
      />

      {/* Requires Timer card */}
      <InfoCard
        icon={<AlertCircle className="size-5 text-(--color-text-secondary)" />}
        label={t`Requires Timer`}
        value={
          <p className="text-base font-medium text-(--color-text-primary)">
            {step.timerEnabled ? t`Yes` : t`No`}
          </p>
        }
        footer={<InfoNote>{t`If true, Cook Mode highlights urgency.`}</InfoNote>}
      />
    </div>
  );
}

function InfoCard({
  icon,
  label,
  value,
  footer,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex h-38.5 flex-1 flex-col items-start justify-between rounded-[8px] bg-(--color-surface-content) p-3">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-[6px] bg-(--color-surface-muted)">
            {icon}
          </div>
          <span className="text-sm text-(--color-text-secondary)">{label}</span>
        </div>
        {value}
      </div>
      {footer}
    </div>
  );
}

function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-9 items-center justify-center rounded-[8px] border border-(--color-border) bg-(--color-surface-content)">
        <Info className="size-5 text-(--color-text-secondary)" />
      </div>
      <p className="min-w-0 flex-1 text-xs leading-snug text-(--color-text-primary)">{children}</p>
    </div>
  );
}

function TextDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 px-3 py-2">
      <p className="text-base font-medium text-(--color-text-primary)">{label}</p>
      <p className="text-sm text-(--color-text-secondary)">{value}</p>
    </div>
  );
}
